import { useToast } from '@hover/blueprint';
import { useEffect, useState } from 'react';
import { generatePath, useHistory } from 'react-router';

import { Designer } from 'src/shared/constants/FeatureFlag';
import {
  JobCaptureTypes,
  usePropertyDetailsQuery,
} from 'src/shared/graphql/generated';
import { request } from 'src/shared/helpers/request';
import {
  TranslationNamespace,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { Route } from 'src/shared/route';
import { EventType, emitPageAction } from 'src/shared/telemetry';

import { useHomeownerAppContext } from './useHomeownerAppContext';
import { useUploadOriginalImage } from './useUploadOriginalImage';

const GENERATED_AI_IMAGES_JOBS = 'generatedAIImages';

type GeneratedAIImagesJobId = {
  jobId: string;
  originalImageId: number;
};

export const useAutoGenerateAIImage = ({ jobId }: { jobId?: string }) => {
  const history = useHistory();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { user } = useHomeownerAppContext();
  const [hasTriedAutoGenerate, setHasTriedAutoGenerate] = useState(false);
  const threeDFusionTreatment = useTreatment(Designer.ENABLED_3D_FUSION);
  const isThreeDFusionReady = typeof threeDFusionTreatment === 'string';
  const isThreeDFusionEnabled = threeDFusionTreatment === 'on';

  const errorToast = useToast({
    duration: 5000,
    isClosable: true,
  });

  const { data } = usePropertyDetailsQuery({
    skip: !jobId,
    variables: {
      id: Number(jobId),
    },
  });

  const job = data?.jobs?.results?.[0];

  const getLocalStorageGeneratedAIImages =
    (): Array<GeneratedAIImagesJobId> => {
      const localStorageGeneratedAIImages = localStorage.getItem(
        GENERATED_AI_IMAGES_JOBS,
      );
      if (localStorageGeneratedAIImages) {
        const generatedAIImages = JSON.parse(
          localStorageGeneratedAIImages,
        ) as Array<GeneratedAIImagesJobId>;
        return generatedAIImages;
      }
      return [];
    };

  const getFileFromUrl = async (url: string) => {
    const defaultType = 'image/jpeg';
    const response = await request(url);
    const fileName = `${jobId!}_front_image.jpeg`;
    const fileData = await response.blob();
    return new File([fileData], fileName, {
      type: fileData.type || defaultType,
    });
  };

  const uploadImage = useUploadOriginalImage();

  const downloadHeroImage = async (): Promise<File> => {
    const heroImage =
      job?.bestFrontImage?.rotatedImageMediumUrl ||
      job?.bestFrontImage?.rotatedImageSmallUrl;

    const file = await getFileFromUrl(heroImage!);
    return file;
  };

  const redirectToHomeownerOriginalPage = ({
    newOriginalImageId,
    from,
  }: {
    newOriginalImageId: number;
    from?: string;
  }) => {
    history.push(
      generatePath(Route.HOMEOWNER_APP_ORIGINAL.path, {
        aiOriginalImageId: newOriginalImageId,
      }),
      { from },
    );
  };

  const autoGenerateAI = async () => {
    try {
      const file = await downloadHeroImage();

      const uploadedImage = await uploadImage({
        image: file,
        email: user?.email,
        spaceType: 'exterior',
        jobClientIdentifier: job?.clientIdentifier,
      });

      const newOriginalImageId = uploadedImage?.id;

      if (newOriginalImageId) {
        const generatedAIImagesJobIds = getLocalStorageGeneratedAIImages();
        const newGeneratedAIImagesJobIds = [
          ...generatedAIImagesJobIds,
          {
            jobId: jobId!,
            originalImageId: newOriginalImageId,
          },
        ];
        localStorage.setItem(
          GENERATED_AI_IMAGES_JOBS,
          JSON.stringify(newGeneratedAIImagesJobIds),
        );

        emitPageAction(EventType.DESIGNER_ORIGINAL_IMAGE_UPLOADED, {
          aiOriginalImageId: `${newOriginalImageId}`,
        });
        redirectToHomeownerOriginalPage({ newOriginalImageId, from: 'upload' });
      }
    } catch (e) {
      errorToast({
        status: 'error',
        description: t('uploadPhotoSheet.uploadErrorMessage'),
      });
    }
  };

  useEffect(() => {
    if (hasTriedAutoGenerate || !isThreeDFusionReady || isThreeDFusionEnabled) {
      return;
    }

    const generatedAIImages = getLocalStorageGeneratedAIImages();
    if (generatedAIImages) {
      const generatedJobId = generatedAIImages.find(
        image => image.jobId === jobId,
      );
      if (generatedJobId) {
        redirectToHomeownerOriginalPage({
          newOriginalImageId: generatedJobId.originalImageId,
        });
        return;
      }
    }

    if (
      job?.captureType &&
      [JobCaptureTypes.DenseExterior, JobCaptureTypes.SparseExterior].includes(
        job?.captureType,
      ) &&
      (job?.bestFrontImage?.rotatedImageMediumUrl ||
        job?.bestFrontImage?.rotatedImageSmallUrl)
    ) {
      setHasTriedAutoGenerate(true);
      void autoGenerateAI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, isThreeDFusionReady, isThreeDFusionEnabled]);
};
